import Vec3 from "../../../shared/vec3";
import * as BABYLON from "babylonjs";
import AssetLoader from "../helper/asset-loader";
import ExpositionRendering from "./ExpositionRendering";
import { Orientation } from "../../../shared/interfaces";
import ShadowGenerator = BABYLON.ShadowGenerator;

export function createBoxMesh(name: string, position: Vec3, height: number, width: number, depth: number, material: BABYLON.StandardMaterial, scene: BABYLON.Scene, shadowGenerator: ShadowGenerator | undefined) {
  const newObject = BABYLON.MeshBuilder.CreateBox(name, { height: height, width: width, depth: depth }, scene);
  newObject.position.x = position.x;
  newObject.position.y = position.y + (height / 2); // ground level
  newObject.position.z = position.z;
  newObject.material = material;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(newObject, shadowGenerator)
  }
  return newObject;
}

export function createSphereMesh(name: string, position: Vec3, segments: number, diameter: number, material: BABYLON.StandardMaterial, scene: BABYLON.Scene, shadowGenerator: ShadowGenerator | undefined) {
  const newObject = BABYLON.Mesh.CreateSphere(name, segments, diameter, scene, false, BABYLON.Mesh.FRONTSIDE);
  newObject.position.x = position.x;
  newObject.position.y = position.y + (diameter / 2);
  newObject.position.z = position.z;
  newObject.material = material;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(newObject, shadowGenerator)
  }
  return newObject;
}

export function createTableMesh(name: string, position: Vec3, material: BABYLON.StandardMaterial, scene: BABYLON.Scene, shadowGenerator: ShadowGenerator) {
  const table = new BABYLON.TransformNode(name);
  table.position.x = position.x;
  table.position.y = position.y;
  table.position.z = position.z;

  const legMaterial = new BABYLON.StandardMaterial("legMaterial", this.scene);
  legMaterial.diffuseColor = BABYLON.Color3.FromHexString('#777777');

  const tableLegHeight = 0.6;
  const tableLeg = BABYLON.Mesh.CreateCylinder(name, tableLegHeight, 0.4, 0.4, 16, 1, scene);
  tableLeg.position.y = 0.5 * tableLegHeight;
  tableLeg.material = legMaterial;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(tableLeg, shadowGenerator);
  }
  tableLeg.parent = table;

  const tableArea = BABYLON.Mesh.CreateCylinder(name, 0.1, 1.5, 1.5, 24, 1, scene);
  tableArea.position.y = tableLegHeight;
  tableArea.material = material;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(tableArea, shadowGenerator);
  }
  tableArea.parent = table;

  return table;
}

export function createBanner(name: string, position: Vec3, scene: BABYLON.Scene, label: string, shadowGenerator: ShadowGenerator) {
  const measurementTexture = new BABYLON.DynamicTexture(name + "-measure-texture", {}, scene, false);
  const dynamicTextureCtx = measurementTexture.getContext();
  const font = "bold 25px monospace";
  dynamicTextureCtx.font = font;
  const fontWidth = dynamicTextureCtx.measureText(label).width;
  //console.log('font width', fontWidth, label);

  const basicResolution = 128;
  const width = Math.max(1, 1 * ((fontWidth + 20) / basicResolution)) * 1.1;
  const height = 0.5;
  const bannerLegHeight = 2.5;
  const fontOffset = 0.5 * (basicResolution * width - fontWidth);

  const plane = BABYLON.MeshBuilder.CreatePlane(name, { height, width }, scene);
  plane.rotationQuaternion = BABYLON.Quaternion.RotationAxis(new BABYLON.Vector3(0, 1, 0), 0);


  // Texture resolution
  const options = {
    width: basicResolution * width,
    height: 0.5 * basicResolution * height
  }

  const bannerMaterial = new BABYLON.StandardMaterial(name + "bannerMaterial", scene);
  const dynamicTexture = new BABYLON.DynamicTexture(name + "-texture", options, scene, false);


  bannerMaterial.diffuseTexture = dynamicTexture;
  plane.material = bannerMaterial;
  plane.position.y = bannerLegHeight - 0.5 * height;

  dynamicTexture.drawText(label, fontOffset, 25, font, "black", "#ffffff", true, true);


  const planeHolder = new BABYLON.TransformNode(name + "-holder");
  plane.parent = planeHolder;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(plane, shadowGenerator);
  }

  const legMaterial = new BABYLON.StandardMaterial("legMaterial", this.scene);
  legMaterial.diffuseColor = BABYLON.Color3.FromHexString('#555555');

  const bannerLeg1 = BABYLON.Mesh.CreateCylinder(name, bannerLegHeight, 0.05, 0.05, 16, 1, scene);
  bannerLeg1.position.y = 0.5 * bannerLegHeight;
  bannerLeg1.material = legMaterial;
  bannerLeg1.position.x = -0.5 * width;
  bannerLeg1.parent = planeHolder;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(bannerLeg1, shadowGenerator);
  }

  const bannerLeg2 = BABYLON.Mesh.CreateCylinder(name, bannerLegHeight, 0.05, 0.05, 16, 1, scene);
  bannerLeg2.position.y = 0.5 * bannerLegHeight;
  bannerLeg2.material = legMaterial;
  bannerLeg2.position.x = 0.5 * width;
  bannerLeg2.parent = planeHolder;
  if (shadowGenerator) {
    ExpositionRendering.addShadows(bannerLeg2, shadowGenerator);
  }

  planeHolder.position.x = position.x - 0.5;
  planeHolder.position.y = position.y;
  planeHolder.position.z = position.z - 0.5;
  planeHolder.rotation.y = Math.PI / 2;

  return planeHolder;
}

export function rotateAsset(positionx: number, rotationOffset: number = 0) {
  let rotation = (Math.PI / 2) + (Math.PI / 2 * rotationOffset);
  if (positionx < 0) {
    rotation = (-1 * Math.PI / 2) - (-1 * Math.PI / 2 * rotationOffset);
  }
  return rotation;
}

export function loadAsset(assetLoader: AssetLoader, name: string, assetIdentifier: string, scale: Vec3, position: Vec3, rotationOffset: number = 0) {
  const newObject = assetLoader.models[assetIdentifier].clone(name, null);
  newObject.scaling = new BABYLON.Vector3(scale.x, scale.y, scale.z);
  newObject.position.x = position.x;
  newObject.position.y = position.y;
  newObject.position.z = position.z;
  newObject.rotate(BABYLON.Axis.Y, rotateAsset(position.x, rotationOffset), BABYLON.Space.LOCAL);
  return newObject;
}


export function getObjectsToInteractAroundMe(playerPosition: Vec3, objects: any[]): any[] {
  const distanceThreshold = 1.5;
  const objectsInThreshold: any[] = []

  objects.forEach((object) => {
    const distance = BABYLON.Vector3.Distance(
        new BABYLON.Vector3(object.position.x, object.position.y, object.position.z),
        new BABYLON.Vector3(playerPosition.x, playerPosition.y, playerPosition.z)
    )
    if (distance <= distanceThreshold) {
      objectsInThreshold.push({ object, distance })
    }
  })

  objectsInThreshold.sort((a, b) => a.distance < b.distance ? -1 : a.distance > b.distance ? +1 : 0)

  return objectsInThreshold.map(data => data.object);
}